import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import every from 'lodash/every';
import { FILTER_TYPES } from 'common/constants/filters';
import { omit } from 'lodash';

const clearEmptyValues = obj => {
  if (Object.keys(obj).length) {
    Object.keys(obj).forEach(
      key =>
        (obj[key] === undefined || obj[key].length === 0) && delete obj[key]
    );
  }
};

export const isEmptyObjectAndKeys = (obj = {}) => {
  if (isEmpty(obj)) return true;

  return every(
    Object.entries(obj).map(([key, value]) => value.length),
    item => item === 0
  );
};

const formatProjectTeamAreasToBoards = (projectTeamAreas = []) => {
  return projectTeamAreas.map(entity => {
    const { project, team, areas } = entity;
    return {
      provider_id: team.id,
      name: team.name,
      project: {
        provider_id: project.id,
        name: project.name
      },
      areas: areas.map(area => ({
        provider_id: area.id,
        name: area.name,
        is_custom: !!area.is_custom
      }))
    };
  });
};

export const formatBoardsToProjectTeamAreas = boards => {
  return boards.map(entity => {
    const { provider_id, name, project, areas } = entity;

    return {
      project: {
        id: project.provider_id,
        name: project.name
      },
      team: {
        id: provider_id,
        name
      },
      areas: areas.map(area => ({
        id: area.provider_id,
        name: area.name,
        is_custom: !!area.is_custom
      }))
    };
  });
};

const formatFiltersToConfiguration = ({ selectedFilters }) => {
  const MAIN_CHILDREN_FILTERS = [
    FILTER_TYPES.BOARD,
    FILTER_TYPES.TEAMS,
    FILTER_TYPES.DYNAMIC_ISSUES,
    FILTER_TYPES.SHORTCUT_EPICS,
    FILTER_TYPES.SHORTCUT_LABEL,
    FILTER_TYPES.PROJECTS
  ];
  const omitedFilters = omit(
    selectedFilters,
    FILTER_TYPES.AZURE_BOARDS_PROJECT_TEAM_AREAS
  );

  const resolvedFilters = Object.entries(omitedFilters).reduce(
    (acc, [key, value]) => {
      acc[key] = value.map(filter =>
        MAIN_CHILDREN_FILTERS.includes(key)
          ? {
              ...(filter.id && { provider_id: filter.id.toString() }),
              ...(filter.entity_type && { type: filter.entity_type }),
              name: filter.title
            }
          : filter.title
      );

      return acc;
    },
    {}
  );

  const projectTeamAreas =
    selectedFilters[FILTER_TYPES.AZURE_BOARDS_PROJECT_TEAM_AREAS] || [];
  const projectTeamAreasAsBoards =
    formatProjectTeamAreasToBoards(projectTeamAreas);

  const {
    board: boards,
    epic: epics,
    label: labels,
    fixVersion: fixVersions,
    dynamic_issues: parentIssues,
    teams,
    projects,
    shortcutLabels,
    shortcutEpics,
    ...fields
  } = resolvedFilters;

  const fieldsLength = Object.keys(fields).length;

  const formattedParentIssues = parentIssues?.map(
    ({ provider_id, name, type }) => ({
      provider_id,
      include_all: true,
      title: name,
      type
    })
  );

  const formattedFilters = {
    boards,
    epics,
    labels,
    fixVersions,
    teams,
    projects,
    shortcutLabels,
    shortcutEpics,
    ...(!!projectTeamAreasAsBoards?.length && {
      boards: projectTeamAreasAsBoards
    }),
    ...(fieldsLength > 0 ? { fields } : {}),
    ...(formattedParentIssues?.length
      ? { parent_issues: formattedParentIssues }
      : {})
  };

  clearEmptyValues(formattedFilters);
  if (formattedFilters.fields) {
    clearEmptyValues(formattedFilters.fields);
  }

  return formattedFilters;
};

export const selectedFiltersToUpdateConfigurationBodyConverter = ({
  selectedFilters,
  selectedMethod,
  selectedMethodDays,
  includeIterations = [],
  excludeIterations = [],
  includeAll = false
}) => {
  const formattedFilters = formatFiltersToConfiguration({ selectedFilters });

  const result = {
    sprint_excludes: excludeIterations,
    sprint_includes: includeIterations,
    cadence: {
      type: selectedMethod,
      days: selectedMethodDays
    },
    ...formattedFilters
  };

  return result;
};

export const shouldSaveToPmImportStrategy = (
  projectsToPmImportStrategy = [],
  epicsToPmImportStrategy = [],
  pmImportSettings = {}
) => {
  const { epics = [], projects = [] } = pmImportSettings;
  return (
    !isEqual(epics, epicsToPmImportStrategy) ||
    !isEqual(projects, projectsToPmImportStrategy)
  );
};
